import React, { useState, useEffect, useReducer, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Bookmark,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import MessageReactions from "../MessageReactions";
import PinnedMessagesBar from "../PinnedMessagesBar";
import { i18n } from "../../translate/i18n";
import {
  onPendingMessage,
  resolvePendingMessage,
} from "../../services/pendingMessages";

import { belongsToConversation } from "../../utils/messageConversation";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    backgroundColor: theme.palette.type === "dark" ? "#111827" : theme.palette.background.default,
    backgroundBlendMode: theme.palette.type === "dark" ? "multiply" : "normal",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#1f2937" : "#ffffff",
    color: theme.palette.type === "dark" ? theme.palette.text.primary : "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 1px rgba(0, 0, 0, 0.4)"
        : "0 1px 1px #b3b3b3",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#111827" : "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedMedia: {
    display: "block",
    width: 96,
    height: 72,
    marginBottom: 6,
    borderRadius: 6,
    objectFit: "cover",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#14532d" : "#dcf8c6",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 1px rgba(0, 0, 0, 0.35)"
        : "0 1px 1px #b3b3b3",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#166534" : "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "flex",
    position: "absolute",
    top: 2,
    right: 3,
    width: 20,
    height: 20,
    padding: 0,
    minWidth: 20,
    color: theme.palette.text.secondary,
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: 0.72,
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.45)"
        : "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.text.secondary,
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(30, 41, 59, 0.9)" : "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 1px rgba(0, 0, 0, 0.35)"
        : "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ticketDivider: {
    alignSelf: "center",
    textAlign: "center",
    maxWidth: "80%",
    margin: "12px 10px 6px",
    padding: "4px 12px",
    borderRadius: "10px",
    fontSize: 12,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(30, 41, 59, 0.9)" : "#f1e7c8",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 1px rgba(0, 0, 0, 0.35)"
        : "0 1px 1px #b3b3b3",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackFailedIcon: {
    color: theme.palette.error.main,
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const newMessageCreatedAt = new Date(newMessage.createdAt).getTime();
    const matchingPendingIndex = state.findIndex((message) => {
      if (!message._isOptimistic) return false;
      if (newMessage._clientMessageId) {
        return message.id === newMessage._clientMessageId;
      }
      if (message.body !== newMessage.body) return false;
      const pendingCreatedAt = new Date(message.createdAt).getTime();
      return Math.abs(newMessageCreatedAt - pendingCreatedAt) <= 60000;
    });
    if (matchingPendingIndex !== -1) {
      state.splice(matchingPendingIndex, 1);
    }
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "ADD_PENDING_MESSAGE") {
    if (!state.some((message) => message.id === action.payload.id)) {
      state.push(action.payload);
    }
    return [...state];
  }

  if (action.type === "MARK_PENDING_FAILED") {
    const messageIndex = state.findIndex((message) => message.id === action.payload.id);
    if (messageIndex !== -1) {
      state[messageIndex] = {
        ...state[messageIndex],
        _sendStatus: "failed",
      };
    }
    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  // Reconsulta tras una reconexión del socket: agrega lo que llegó mientras
  // la conexión estuvo caída sin duplicar ni desordenar lo ya mostrado.
  if (action.type === "MERGE_MESSAGES") {
    const incoming = action.payload;
    let changed = false;

    incoming.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        state.push(message);
        changed = true;
      }
    });

    if (!changed) return [...state];

    const confirmed = state.filter((message) => !message._isOptimistic);
    const optimistic = state.filter((message) => message._isOptimistic);
    confirmed.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    return [...confirmed, ...optimistic];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup, reactionTicketId }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadingMessageId, setDownloadingMessageId] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const conversationRef = useRef({ id: ticketId });

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
    conversationRef.current = { id: ticketId };
  }, [ticketId]);

  useEffect(() => {
    let active = true;
    const request = axios.CancelToken.source();
    setLoading(true);
    const fetchMessages = async () => {
      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber },
          cancelToken: request.token,
        });
        if (!active || currentTicketId.current !== ticketId) return;

        conversationRef.current = data.ticket || { id: ticketId };
        dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
        setHasMore(data.hasMore);
        setLoading(false);

        if (pageNumber === 1 && data.messages.length > 1) {
          scrollToBottom();
        }
      } catch (err) {
        if (!active || axios.isCancel(err)) return;
        setLoading(false);
        toastError(err);
      }
    };
    fetchMessages();
    return () => {
      active = false;
      request.cancel();
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    let active = true;
    const request = axios.CancelToken.source();
    setPinnedMessages([]);
    const fetchPinnedMessages = async () => {
      try {
        const { data } = await api.get(`/messages/${ticketId}/pinned`, {
          cancelToken: request.token,
        });
        if (!active) return;
        const pinned = Array.isArray(data) ? data : [];
        setPinnedMessages(pinned);
        pinned.forEach(message => {
          dispatch({ type: "UPDATE_MESSAGE", payload: message });
        });
      } catch (err) {
        if (!active || axios.isCancel(err)) return;
        toastError(err);
      }
    };

    fetchPinnedMessages();
    return () => {
      active = false;
      request.cancel();
    };
  }, [ticketId]);

  useEffect(() => {
    const socket = openSocket();
    let connectedBefore = false;
    let active = true;

    socket.on("connect", () => {
      if (!active) return;
      socket.emit("joinChatBox", ticketId);

      // En la primera conexión la lista se carga por la consulta normal. En
      // las siguientes (reconexión) hay que volver a pedir la última página:
      // los mensajes emitidos mientras el socket estuvo caído no se reciben.
      if (!connectedBefore) {
        connectedBefore = true;
        return;
      }

      api
        .get("/messages/" + ticketId, { params: { pageNumber: 1 } })
        .then(({ data }) => {
          if (!active || currentTicketId.current !== ticketId) return;
          dispatch({ type: "MERGE_MESSAGES", payload: data.messages });
        })
        .catch(() => {});
    });

    socket.on("appMessage", (data) => {
      if (!active) return;
      // Confirmar el envío pendiente aunque ya se esté viendo otro ticket.
      if (data?.action === "create" && data.message) {
        resolvePendingMessage(data.message._clientMessageId);
      }
      // Las salas de bandeja también emiten mensajes de otros tickets.
      // Aceptar solo la conversación visible, incluido su historial anterior.
      if (!belongsToConversation(data?.message, conversationRef.current)) {
        return;
      }
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
        setPinnedMessages(current => {
          const withoutMessage = current.filter(item => item.id !== data.message.id);
          const isStillPinned =
            data.message.isPinned &&
            (!data.message.pinnedUntil ||
              new Date(data.message.pinnedUntil).getTime() > Date.now());
          return isStillPinned ? [...withoutMessage, data.message] : withoutMessage;
        });
      }
    });

    return () => {
      active = false;
      socket.disconnect();
    };
  }, [ticketId]);

  useEffect(() => onPendingMessage((event) => {
    if (Number(event.ticketId) !== Number(ticketId)) return;
    if (event.action === "add") {
      dispatch({ type: "ADD_PENDING_MESSAGE", payload: event.message });
    }
    if (event.action === "failed") {
      dispatch({ type: "MARK_PENDING_FAILED", payload: event.message });
    }
  }), [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const lastMessageId = messagesList[messagesList.length - 1]?.id;
  useEffect(() => {
    scrollToBottom();
  }, [lastMessageId]);

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = () => {
    setAnchorEl(null);
  };

  const getDownloadFileName = (message) => {
    let storedFileName = "archivo";

    try {
      const mediaPath = new URL(message.mediaUrl, window.location.origin).pathname;
      storedFileName = decodeURIComponent(mediaPath.split("/").pop()) || storedFileName;
    } catch {
      // Keep a safe fallback if WhatsApp provides a malformed URL.
    }

    const body = String(message.body || "").trim();
    const extension = storedFileName.match(/\.[a-z0-9]{1,10}$/i)?.[0];
    const hasOriginalName =
      body && extension && body.toLowerCase().endsWith(extension.toLowerCase());
    const fileName = hasOriginalName ? body : storedFileName;

    const safeFileName = fileName
      .split(/[\\/]/)
      .pop()
      .replace(/[<>:"|?*]/g, "_");

    return Array.from(safeFileName, (character) =>
      character.charCodeAt(0) < 32 ? "_" : character
    ).join("");
  };

  const handleDownloadMedia = async (message) => {
    setDownloadingMessageId(message.id);

    try {
      const { data, headers } = await api.get(message.mediaUrl, {
        responseType: "blob",
      });
      const blob = data instanceof Blob
        ? data
        : new Blob([data], { type: headers["content-type"] });
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");

      downloadLink.href = objectUrl;
      downloadLink.download = getDownloadFileName(message);
      downloadLink.style.display = "none";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      toast.error(i18n.t("messagesList.downloadError"), {
        toastId: "media-download-error",
      });
    } finally {
      setDownloadingMessageId((currentId) =>
        currentId === message.id ? null : currentId
      );
    }
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (
      /\.(jpe?g|png|gif|webp)(?:[?#].*)?$/i.test(message.mediaUrl || "") &&
      (message.mediaType === "image" || message.mediaType === "sticker")
    ) {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={
                downloadingMessageId === message.id
                  ? <CircularProgress size={18} />
                  : <GetApp />
              }
              color="primary"
              variant="outlined"
              disabled={downloadingMessageId !== null}
              onClick={() => handleDownloadMedia(message)}
            >
              {downloadingMessageId === message.id
                ? i18n.t("messagesList.downloading")
                : i18n.t("messagesList.download")}
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message._sendStatus === "failed") {
      return (
        <Block
          fontSize="small"
          className={classes.ackFailedIcon}
          titleAccess={i18n.t("messagesList.delivery.failed")}
        />
      );
    }
    if (message.ack === 0) {
      return (
        <AccessTime
          fontSize="small"
          className={classes.ackIcons}
          titleAccess={i18n.t("messagesList.delivery.pending")}
        />
      );
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
  };

  // La conversación incluye el historial guardado de tickets anteriores del
  // mismo contacto; se marca cada cambio de ticket para que el operario sepa
  // qué parte corresponde al ticket abierto y cuál a atenciones pasadas.
  const renderTicketDivider = (message, index) => {
    const currentTicketNumber = Number(ticketId);
    const messageTicketId = Number(message.ticketId);
    if (!messageTicketId) return null;

    const previousTicketId =
      index > 0 ? Number(messagesList[index - 1].ticketId) : null;
    const startsNewTicket =
      index === 0
        ? messageTicketId !== currentTicketNumber
        : previousTicketId !== messageTicketId;

    if (!startsNewTicket) return null;

    const label =
      messageTicketId === currentTicketNumber
        ? i18n.t("messagesList.history.currentTicket", {
            ticketId: messageTicketId,
          })
        : i18n.t("messagesList.history.previousTicket", {
            ticketId: messageTicketId,
          });

    return (
      <span className={classes.ticketDivider} key={`ticket-${message.id}`}>
        {label}
      </span>
    );
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.mediaType === "image" &&
            message.quotedMsg?.mediaUrl && (
              <img
                className={classes.quotedMedia}
                src={message.quotedMsg.mediaUrl}
                alt="Imagen citada"
              />
            )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderTicketDivider(message, index)}
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageLeft}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  title={i18n.t("messageOptionsMenu.open")}
                  aria-label={i18n.t("messageOptionsMenu.open")}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore style={{ fontSize: 18 }} />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {!message.isDeleted && (message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {message.mediaType !== "location" && (
                    <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  )}
                  <MessageReactions reactions={message.reactions} />
                  {message.isPinned && (!message.pinnedUntil || new Date(message.pinnedUntil).getTime() > Date.now()) && (
                    <Bookmark
                      fontSize="small"
                      style={{ color: message.pinSynced ? "#16a34a" : "#f59e0b" }}
                      titleAccess={i18n.t(message.pinSynced ? "pinnedMessages.synced" : "pinnedMessages.localOnly")}
                    />
                  )}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderTicketDivider(message, index)}
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageRight}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  title={i18n.t("messageOptionsMenu.open")}
                  aria-label={i18n.t("messageOptionsMenu.open")}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore style={{ fontSize: 18 }} />
                </IconButton>
                {!message.isDeleted && (message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {(message.isDeleted || message.mediaType !== "location") && (
                    <MarkdownWrapper>
                      {message.isDeleted ? "Mensaje eliminado" : message.body}
                    </MarkdownWrapper>
                  )}
                  <MessageReactions reactions={message.reactions} />
                  {message.isPinned && (!message.pinnedUntil || new Date(message.pinnedUntil).getTime() > Date.now()) && (
                    <Bookmark
                      fontSize="small"
                      style={{ color: message.pinSynced ? "#16a34a" : "#f59e0b" }}
                      titleAccess={i18n.t(message.pinSynced ? "pinnedMessages.synced" : "pinnedMessages.localOnly")}
                    />
                  )}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        ticketId={ticketId}
        reactionTicketId={reactionTicketId}
      />
      <PinnedMessagesBar messages={pinnedMessages} />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
        <div ref={lastMessageRef} style={{ clear: "both" }} />
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
